/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSetupDialog, uiSelectors, uiTypes } from '@opengeoweb/store';
import { TimeSeriesSelect } from './TimeSeriesSelect';
import { timeSeriesActions } from '../../store';
import { getPlotWithParameters, getServices } from '../../store/selectors';
import type { TimeSeriesRootState } from '../../store/types';
import { TimeSeriesActionOrigin } from '../../store/types';
import type { Parameter, Plot } from '../TimeSeries/types';

const DIALOG_TYPE = uiTypes.DialogTypes.TimeSeriesSelect;
const origin = TimeSeriesActionOrigin.timeSeriesSelect;

interface TimeSeriesSelectConnectProps {
  selectPlotId: string;
  panelId: string;
}

export const TimeSeriesSelectConnect: React.FC<
  TimeSeriesSelectConnectProps
> = ({ selectPlotId, panelId }) => {
  const { setDialogOrder, dialogOrder, isDialogOpen, onCloseDialog } =
    useSetupDialog(DIALOG_TYPE);
  const activePanelId = useSelector((state: TimeSeriesRootState) =>
    uiSelectors.getSourcePanelId(state, DIALOG_TYPE),
  );

  const dispatch = useDispatch();

  const plot: Plot | undefined = useSelector((state: TimeSeriesRootState) =>
    getPlotWithParameters(state, selectPlotId, panelId),
  );

  // If select dialog for a plot is open but the plot is deleted, close the dialog
  React.useEffect(() => {
    if (isDialogOpen && activePanelId === panelId && !plot) {
      onCloseDialog();
    }
  }, [plot, isDialogOpen, onCloseDialog, activePanelId, panelId]);

  const handleAddOrRemoveClick = React.useCallback(
    (parameter: Parameter, plotHasParameter: boolean): void => {
      if (plotHasParameter) {
        dispatch(
          timeSeriesActions.deleteParameter({
            panelId,
            origin,
            parameterId: parameter.id!,
          }),
        );
      } else {
        dispatch(
          timeSeriesActions.addParameter({ panelId, origin, parameter }),
        );
      }
    },
    [dispatch, panelId],
  );

  const services = useSelector((state: TimeSeriesRootState) =>
    getServices(state),
  );

  const isDialogOpenForPanel = isDialogOpen && activePanelId === panelId;

  return plot && services && services.length > 0 ? (
    <TimeSeriesSelect
      handleAddOrRemoveClick={handleAddOrRemoveClick}
      isOpen={isDialogOpenForPanel}
      onClose={onCloseDialog}
      onMouseDown={setDialogOrder}
      order={dialogOrder}
      panelId={panelId}
      selectPlot={plot}
      services={services}
    />
  ) : null;
};
